import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

function Counter({ to, suffix = "", duration = 1600, decimals = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const from = 0;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(from + (to - from) * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration]);

  return (
    <span ref={ref} className="tabular-nums">
      {decimals ? val.toFixed(decimals) : Math.round(val).toLocaleString()}
      {suffix}
    </span>
  );
}

function Sparkline({ points, accent = "#10B981" }) {
  // simple normalized polyline
  const w = 90, h = 24;
  const max = Math.max(...points);
  const min = Math.min(...points);
  const range = max - min || 1;
  const step = w / (points.length - 1);
  const d = points
    .map((p, i) => {
      const x = i * step;
      const y = h - ((p - min) / range) * h;
      return `${i === 0 ? "M" : "L"} ${x} ${y}`;
    })
    .join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="opacity-70">
      <path d={d} fill="none" stroke={accent} strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

const stats = [
  { label: "Damaging reviews removed", value: 2847, suffix: "+", spark: [3, 5, 4, 7, 9, 12, 15, 18, 22, 26], accent: "#10B981" },
  { label: "Avg. rating lift", value: 1.4, suffix: "★", decimals: 1, spark: [3.1, 3.2, 3.4, 3.7, 4.0, 4.2, 4.4, 4.5, 4.6, 4.7], accent: "#D4AF37" },
  { label: "Businesses protected", value: 640, suffix: "+", spark: [2, 3, 5, 6, 8, 11, 13, 15, 17, 19], accent: "#10B981" },
  { label: "Success rate", value: 92, suffix: "%", spark: [78, 82, 84, 86, 88, 90, 91, 91, 92, 92], accent: "#D4AF37" },
];

export default function TrustBar() {
  return (
    <section className="relative border-y border-white/5 bg-white/[0.015]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 grid grid-cols-2 lg:grid-cols-4 gap-8">
        {stats.map((s, i) => (
          <motion.div
            key={s.label}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.5, delay: i * 0.08 }}
            className="group relative flex flex-col gap-1 pl-4 border-l border-white/5 hover:border-[#10B981]/40 transition-colors"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="font-display text-3xl sm:text-4xl font-bold text-white bg-gradient-to-r from-white to-white bg-clip-text group-hover:from-[#10B981] group-hover:to-[#D4AF37] group-hover:text-transparent transition-colors">
                <Counter to={s.value} suffix={s.suffix} decimals={s.decimals || 0} />
              </div>
              <Sparkline points={s.spark} accent={s.accent} />
            </div>
            <div className="text-xs uppercase tracking-[0.14em] text-white/40 group-hover:text-white/60 transition-colors">
              {s.label}
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
